import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, ClipboardList, Check, HelpCircle, Scale, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import ScoreGauge from '../components/ScoreGauge';
import { withTermTooltips } from '../components/TermTooltip';
import { SCORE_LEVELS } from '../lib/constants';

export default function Diagnostic() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0); // -1 = intro? we use 0 = intro
  // step 0 = intro; step 1..N = questions; step N+1 = summary/result
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/diagnostic/questions');
        setCategories(data.categories);
        setQuestions(data.questions);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const questionMatchesConditions = (q) => {
    if (!q?.show_if) return true;
    for (const [qid, cond] of Object.entries(q.show_if)) {
      const val = answers[qid];
      if (cond.eq && val !== cond.eq) return false;
      if (cond.neq && val === cond.neq) return false;
      if (cond.has_any_except) {
        if (!Array.isArray(val) || val.length === 0) return false;
        const filtered = val.filter((v) => !cond.has_any_except.includes(v));
        if (filtered.length === 0) return false;
      }
    }
    return true;
  };

  // Auto-responder preguntas que están ocultas y traen valor por defecto
  useEffect(() => {
    const patch = {};
    for (const q of questions) {
      if (q.auto_answer_when_hidden && !questionMatchesConditions(q)) {
        if (answers[q.id] !== q.auto_answer_when_hidden) {
          patch[q.id] = q.auto_answer_when_hidden;
        }
      }
    }
    if (Object.keys(patch).length > 0) {
      setAnswers((prev) => ({ ...prev, ...patch }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, questions]);

  const visibleQuestions = useMemo(
    () => questions.filter(questionMatchesConditions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, answers],
  );

  const total = visibleQuestions.length;
  const progress = useMemo(() => {
    if (step === 0) return 0;
    if (result) return 100;
    return Math.round(((step - 1) / total) * 100);
  }, [step, total, result]);

  const currentQ = step > 0 && step <= total ? visibleQuestions[step - 1] : null;
  const answered = currentQ ? answers[currentQ.id] : null;
  const isMulti = currentQ?.type === 'multi_select';

  const setAnswer = (val) => {
    if (!currentQ) return;
    setAnswers((prev) => ({ ...prev, [currentQ.id]: val }));
  };

  const toggleMultiAnswer = (val) => {
    if (!currentQ) return;
    const current = Array.isArray(answers[currentQ.id]) ? answers[currentQ.id] : [];
    let next;
    if (val === 'ninguna') {
      // «Ninguna» es excluyente: si la marca, limpia el resto
      next = current.includes('ninguna') ? [] : ['ninguna'];
    } else {
      next = current.includes(val)
        ? current.filter((x) => x !== val)
        : [...current.filter((x) => x !== 'ninguna'), val];
    }
    setAnswers((prev) => ({ ...prev, [currentQ.id]: next }));
  };

  const isAnsweredValid = () => {
    if (!currentQ) return false;
    if (isMulti) return Array.isArray(answered) && answered.length > 0;
    return !!answered;
  };

  const goNext = async () => {
    if (step === 0) { setStep(1); return; }
    if (step < total) { setStep(step + 1); return; }
    // submit
    setSubmitting(true);
    try {
      const payload = { answers: Object.entries(answers).map(([question_id, value]) => ({ question_id, value })) };
      const { data } = await api.post('/diagnostic/submit', payload);
      setResult(data.diagnostic);
      toast.success('Diagnóstico calculado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos calcular el diagnóstico.');
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => setStep(Math.max(0, step - 1));

  if (loading) {
    return <div className="text-sm text-[#6B7280]">Cargando cuestionario…</div>;
  }

  // ---------- RESULT VIEW ----------
  if (result) {
    const meta = SCORE_LEVELS[result.level] || SCORE_LEVELS.critico;
    return (
      <div className="max-w-5xl mx-auto space-y-8 animate-fade-in" data-testid="diagnostic-result">
        <div className="pv-card p-8 md:p-10 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="pv-eyebrow mb-2">Resultado del diagnóstico</div>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1C2329] tracking-tight">
              Su score inicial de cumplimiento es <span style={{ color: meta.color }}>{result.score}/100</span>
            </h2>
            <p className="text-sm text-[#374151] mt-3 leading-relaxed whitespace-pre-line">
              {result.ai_summary}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button className="pv-btn-primary flex items-center gap-2" onClick={() => navigate('/app/plan')} data-testid="result-view-plan">
                Ver plan de acción <ArrowRight size={16} />
              </button>
              <button className="pv-btn-secondary" onClick={() => navigate('/app/dashboard')} data-testid="result-goto-dashboard">
                Ir al dashboard
              </button>
            </div>
          </div>
          <div className="flex justify-center">
            <ScoreGauge score={result.score} level={result.level} size={260} />
          </div>
        </div>

        <div className="pv-card p-6">
          <div className="pv-eyebrow mb-4">Desglose por categoría</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.category_scores.map((c) => (
              <div key={c.category_id} className="p-4 border border-[#E5E7EB] rounded-md" data-testid={`cat-${c.category_id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[14px] font-medium text-[#1C2329]">{c.category_label}</div>
                  <div className="tabular-nums text-sm font-semibold" style={{ color: SCORE_LEVELS[levelFor(c.score)].color }}>
                    {c.score}/100
                  </div>
                </div>
                <div className="pv-seg-bar mt-3">
                  <span style={{ width: `${c.score}%`, background: SCORE_LEVELS[levelFor(c.score)].color }} />
                </div>
                <div className="text-[11px] text-[#6B7280] mt-2 uppercase tracking-wider">Peso {(c.weight * 100).toFixed(0)}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ---------- INTRO ----------
  if (step === 0) {
    return (
      <div className="max-w-3xl mx-auto animate-fade-in">
        <div className="pv-card p-8 md:p-12">
          <div className="w-12 h-12 rounded-md bg-[#ECFDF5] border border-[#2A9B68]/30 flex items-center justify-center text-[#2A9B68]">
            <ClipboardList size={20} strokeWidth={1.5} />
          </div>
          <h2 className="mt-5 text-2xl md:text-3xl font-semibold text-[#1C2329] tracking-tight">
            Diagnóstico inicial de cumplimiento
          </h2>
          <p className="mt-3 text-[15px] text-[#374151] leading-relaxed">
            Vamos a hacerle {total} preguntas simples sobre cómo su empresa trata datos personales hoy. No hay respuestas correctas ni incorrectas: cada respuesta nos ayuda a construir un plan realista y priorizado.
          </p>

          <div className="mt-6 border-l-4 border-[#F59E0B] bg-[#FFFBEB] px-4 py-3">
            <div className="text-[13.5px] text-[#78350F] leading-relaxed">
              <b>Un consejo antes de empezar:</b> responda con total sinceridad. Si tiene dudas
              sobre una pregunta, marque <b>«No»</b> o <b>«No lo sé»</b>. La idea es identificar
              lo que le falta para poder revisarlo con tranquilidad después — no es un examen.
              Cuanto más honesto sea, más útil será el plan que le vamos a preparar.
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { k: 'Tiempo', v: '10–15 min' },
              { k: 'Preguntas', v: `${total} en 8 categorías` },
              { k: 'Resultado', v: 'Score 0–100 + plan' },
            ].map((it) => (
              <div key={it.k} className="p-4 border border-[#E5E7EB] rounded-md">
                <div className="pv-eyebrow">{it.k}</div>
                <div className="mt-1 text-[15px] font-medium text-[#1C2329]">{it.v}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <button className="pv-btn-secondary flex items-center gap-2" onClick={() => navigate('/app/dashboard')} data-testid="diagnostic-cancel">
              <ArrowLeft size={14} /> Cancelar
            </button>
            <button className="pv-btn-primary flex items-center gap-2" onClick={() => setStep(1)} data-testid="diagnostic-start">
              Comenzar diagnóstico <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- QUESTION VIEW ----------
  const catMeta = categories.find((c) => c.id === currentQ?.category);
  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between text-[12px] text-[#6B7280] mb-3">
        <div className="uppercase tracking-wider">Pregunta {step} de {total}</div>
        <div className="tabular-nums">{progress}% completado</div>
      </div>
      <div className="pv-seg-bar mb-8"><span style={{ width: `${progress}%` }} /></div>

      <div className="pv-card p-8 md:p-10" data-testid={`question-${currentQ.id}`}>
        <div className="pv-eyebrow" style={{ color: '#2A9B68' }}>{catMeta?.label}</div>
        <h2 className="mt-3 text-xl md:text-2xl font-semibold text-[#1C2329] tracking-tight leading-snug">
          {withTermTooltips(currentQ.prompt)}
        </h2>
        {currentQ.help && (
          <div className="mt-3 flex items-start gap-2 text-[13px] text-[#6B7280]">
            <HelpCircle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
            <span className="whitespace-pre-line leading-relaxed">{withTermTooltips(currentQ.help)}</span>
          </div>
        )}

        {currentQ.deep_dive && (
          <details className="mt-4 group border border-[#E5E7EB] bg-[#FAFBFC]" data-testid={`deep-dive-${currentQ.id}`}>
            <summary className="cursor-pointer select-none flex items-center gap-2 px-3 py-2 text-[12.5px] font-medium text-[#0F2A26] hover:bg-[#F0F7F5] transition list-none">
              <Scale size={13} strokeWidth={1.75} className="text-[#065F46]" />
              <span className="flex-1">{currentQ.deep_dive.title}</span>
              <ChevronDown size={13} className="transition-transform group-open:rotate-180 text-[#6B7280]" />
            </summary>
            <div className="px-3 py-3 border-t border-[#E5E7EB] space-y-3 text-[12.5px] text-[#374151] leading-relaxed">
              {currentQ.deep_dive.sections.map((s, i) => (
                <div key={i}>
                  <div className="font-semibold text-[#0F2A26] mb-0.5 text-[12px] uppercase tracking-wider">
                    {s.heading}
                  </div>
                  <div className="whitespace-pre-line">{withTermTooltips(s.body)}</div>
                </div>
              ))}
              <div className="text-[10.5px] text-[#6B7280] italic pt-1 border-t border-[#E5E7EB] mt-3">
                Este bloque es informativo y no reemplaza asesoría legal específica.
              </div>
            </div>
          </details>
        )}

        <div className="mt-8 space-y-3">
          {isMulti
            ? currentQ.options.map((opt) => {
                const checked = Array.isArray(answered) && answered.includes(opt.value);
                const isNinguna = opt.value === 'ninguna';
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleMultiAnswer(opt.value)}
                    className={`w-full flex items-start justify-between px-4 py-3 rounded-md border text-left transition-colors ${checked ? 'border-[#2A9B68] bg-[#ECFDF5]' : 'border-[#E5E7EB] bg-white hover:bg-[#FAFBFC]'} ${isNinguna ? 'border-dashed' : ''}`}
                    data-testid={`option-${currentQ.id}-${opt.value}`}
                  >
                    <span className={`text-[14px] leading-relaxed pr-3 ${checked ? 'text-[#065F46]' : 'text-[#1C2329]'} ${isNinguna ? 'italic' : ''}`}>
                      {opt.label}
                    </span>
                    <span className={`w-5 h-5 mt-0.5 shrink-0 rounded border flex items-center justify-center ${checked ? 'bg-[#2A9B68] border-[#2A9B68]' : 'border-[#D1D5DB]'}`}>
                      {checked && <Check size={12} className="text-white" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })
            : currentQ.options.map((opt) => {
                const selected = answered === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAnswer(opt.value)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-md border text-left transition-colors ${selected ? 'border-[#2A9B68] bg-[#ECFDF5]' : 'border-[#E5E7EB] bg-white hover:bg-[#FAFBFC]'}`}
                    data-testid={`option-${currentQ.id}-${opt.value}`}
                  >
                    <span className={`text-[15px] font-medium ${selected ? 'text-[#065F46]' : 'text-[#1C2329]'}`}>{opt.label}</span>
                    <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${selected ? 'bg-[#2A9B68] border-[#2A9B68]' : 'border-[#D1D5DB]'}`}>
                      {selected && <Check size={12} className="text-white" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <button className="pv-btn-secondary flex items-center gap-2" onClick={goBack} data-testid="diagnostic-back">
          <ArrowLeft size={14} /> Anterior
        </button>
        <button
          className="pv-btn-primary flex items-center gap-2"
          disabled={!isAnsweredValid() || submitting}
          onClick={goNext}
          data-testid="diagnostic-next"
        >
          {step === total ? (submitting ? 'Calculando…' : 'Calcular score') : 'Siguiente'}
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}

function levelFor(score) {
  if (score >= 85) return 'excelente';
  if (score >= 70) return 'bueno';
  if (score >= 50) return 'medio';
  if (score >= 30) return 'bajo';
  return 'critico';
}
